import React, { useEffect, useState } from 'react';
import {
  Anchor,
  Badge,
  Button,
  Group,
  Select,
  Stack,
  Tabs,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { ArrowRight, ChevronDown, ChevronUp, Search } from 'lucide-react';
import {
  Link,
  useLoaderData,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import {
  COVERAGE_REASON_LABELS,
  QUEUE_STAGE_LABELS,
  QUEUE_VIEWS,
  REVIEW_CATEGORIES,
  queueActionLabel,
  reviewNarrative,
  type ReviewQueue,
  type ReviewQueueAlert,
} from '@tinypro-audit/contracts';
import './UnifiedInboxPage.css';
import { usePageRefresh } from '../api/use-page-refresh';

const visibleQueueViews = ['current', 'history', 'all'] as const;

function alertHref(alert: ReviewQueueAlert, returnTo: string) {
  const url = new URL(alert.href, 'http://local');
  url.searchParams.set('from', returnTo);
  return `${url.pathname}${url.search}`;
}
function rememberPosition(returnTo: string) {
  try {
    sessionStorage.setItem(
      `inbox-position:${returnTo}`,
      String(window.scrollY),
    );
  } catch {
    /* Storage can be disabled. */
  }
}
function AlertLabel({ alert }: { alert: ReviewQueueAlert }) {
  const label =
    alert.state === 'resolved'
      ? 'Resolved by reviewer'
      : alert.stage === 'pre_site'
        ? QUEUE_STAGE_LABELS.pre_site
        : alert.stage === 'measurement_applicability'
          ? QUEUE_STAGE_LABELS.measurement_applicability
          : REVIEW_CATEGORIES[alert.category].label;
  return (
    <Badge
      className="inbox-alert-label"
      color={
        alert.state === 'resolved'
          ? 'teal'
          : alert.stage === 'pre_site'
            ? 'orange'
            : alert.stage === 'measurement_applicability'
              ? 'gray'
              : REVIEW_CATEGORIES[alert.category].color
      }
      variant="light"
    >
      {label}
    </Badge>
  );
}
function ReviewAction({
  alert,
  returnTo,
}: {
  alert: ReviewQueueAlert;
  returnTo: string;
}) {
  return (
    <Button
      component={Link}
      to={alertHref(alert, returnTo)}
      onClick={() => rememberPosition(returnTo)}
      variant="light"
      size="sm"
      rightSection={<ArrowRight size={14} />}
    >
      {queueActionLabel(alert)}
    </Button>
  );
}
const reportDate = (value: string) =>
  new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${value}T12:00:00Z`));

export function InboxPage(): React.ReactElement {
  const data = useLoaderData() as ReviewQueue;
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const returnTo = `${location.pathname}${location.search}`;
  const [expanded, setExpanded] = useState<string[]>([]);
  const [search, setSearch] = useState(params.get('search') ?? '');
  const [moreFilters, setMoreFilters] = useState(false);
  usePageRefresh(data.refresh);
  const savedSearch = params.get('search') ?? '';
  useEffect(() => setSearch(savedSearch), [savedSearch]);
  useEffect(() => {
    setExpanded([]);
    let position = 0;
    try {
      position =
        Number(sessionStorage.getItem(`inbox-position:${returnTo}`)) || 0;
    } catch {
      /* Optional position restoration. */
    }
    const frame = requestAnimationFrame(() => window.scrollTo(0, position));
    return () => cancelAnimationFrame(frame);
  }, [returnTo]);
  const update = (
    changes: Record<string, string | null>,
    pagination = false,
  ) => {
    const query = new URLSearchParams(params);
    if (!pagination) query.delete('offset');
    query.set('view', data.view);
    if (search.trim()) query.set('search', search.trim());
    else query.delete('search');
    for (const [key, value] of Object.entries(changes)) {
      if (
        value === null ||
        (value === 'all' && !['view', 'state'].includes(key))
      )
        query.delete(key);
      else query.set(key, value);
    }
    setParams(query);
  };
  const offset = Number(params.get('offset') ?? 0);
  const batch = data.current_collection;
  const stage = params.get('stage') ?? 'all';
  const activeFilters = ['state', 'category', 'coverage_reason'].filter((key) =>
    params.has(key),
  ).length;
  return (
    <Stack gap="md" className="inbox-page">
      <div>
        <Title order={2}>Inbox</Title>
        <Text c="dimmed" size="sm">
          Choose an issue to review its evidence and next steps.
        </Text>
      </div>
      <Tabs
        value={data.view}
        onChange={(value) => value && update({ view: value })}
        className="inbox-views"
      >
        <Tabs.List aria-label="Inbox views">
          {visibleQueueViews.map((value) => (
            <Tabs.Tab
              value={value}
              key={value}
              disabled={value === 'current' && !batch}
              rightSection={
                <span className="inbox-view-count">
                  {data.view_counts[value]}
                </span>
              }
            >
              {QUEUE_VIEWS[value]}
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs>
      <div className="inbox-scope" aria-live="polite">
        {data.view === 'current' && batch ? (
          <>
            <Text size="sm" fw={600}>
              Latest collection · {reportDate(batch.report_start_date)} –{' '}
              {reportDate(batch.report_end_date)}
            </Text>
            <Text size="xs" c="dimmed">
              {batch.timezone} · {batch.project_count} projects linked to this
              collection
              {batch.unconfirmed_project_count
                ? ` · ${batch.unconfirmed_project_count} report identities still unconfirmed`
                : ''}
              . Counts below include projects with review items.
            </Text>
          </>
        ) : (
          <>
            <Text size="sm" fw={600}>
              {data.view === 'history'
                ? 'Stored reviews outside the latest collection'
                : 'All stored reviews · across all dates'}
            </Text>
            <Text size="xs" c="dimmed">
              {data.unconfirmed_count
                ? `${data.unconfirmed_count} entries have unconfirmed collection membership. `
                : ''}
              {data.view === 'history'
                ? 'This view does not establish whether a job is complete.'
                : !batch
                  ? 'No collection batch is available; all reviews are shown.'
                  : 'Includes pre-site form reviews and ordering follow-ups.'}
            </Text>
          </>
        )}
      </div>
      <form
        className="inbox-filters"
        onSubmit={(event) => {
          event.preventDefault();
          update({ search: search.trim() || null });
        }}
      >
        <TextInput
          label="Search"
          placeholder="Project, job, PO or issue"
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          leftSection={<Search size={16} />}
          rightSection={
            <button
              className="inbox-search-submit"
              type="submit"
              aria-label="Search Inbox"
            >
              <ArrowRight size={16} />
            </button>
          }
        />
        <Select
          label="Review area"
          value={stage}
          onChange={(value) => update({ stage: value, coverage_reason: null })}
          data={[
            { value: 'all', label: 'All review areas' },
            { value: 'pre_site', label: QUEUE_STAGE_LABELS.pre_site },
            {
              value: 'measurement_applicability',
              label: QUEUE_STAGE_LABELS.measurement_applicability,
            },
            { value: 'po', label: QUEUE_STAGE_LABELS.po },
            {
              value: 'acknowledgement',
              label: QUEUE_STAGE_LABELS.acknowledgement,
            },
            { value: 'collection', label: 'Missing evidence' },
            { value: 'analysis', label: 'Analysis' },
          ]}
        />
        <Button
          variant="default"
          onClick={() => setMoreFilters((value) => !value)}
          aria-expanded={moreFilters}
          rightSection={
            moreFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />
          }
        >
          Filters{activeFilters ? ` (${activeFilters})` : ''}
        </Button>
        {moreFilters && (
          <div className="inbox-extra-filters">
            <Select
              label="Alert type"
              value={params.get('category') ?? 'all'}
              onChange={(value) => update({ category: value })}
              data={[
                { value: 'all', label: 'All alert types' },
                ...Object.entries(REVIEW_CATEGORIES)
                  .filter(([key]) => !['verified', 'processing'].includes(key))
                  .map(([value, display]) => ({
                    value,
                    label: display.label,
                  })),
              ]}
            />
            {stage === 'measurement_applicability' && (
              <Select
                label="Pre-site follow-up reason"
                value={params.get('coverage_reason') ?? 'all'}
                onChange={(value) => update({ coverage_reason: value })}
                data={[
                  { value: 'all', label: 'All follow-up reasons' },
                  ...Object.entries(COVERAGE_REASON_LABELS).map(
                    ([value, label]) => ({ value, label }),
                  ),
                ]}
              />
            )}
            <Select
              label="State"
              value={params.get('state') ?? 'active'}
              onChange={(value) => update({ state: value })}
              data={[
                { value: 'active', label: 'Active' },
                { value: 'resolved', label: 'Resolved' },
                { value: 'all', label: 'Active and resolved' },
              ]}
            />
            <Button
              variant="subtle"
              onClick={() => {
                setSearch('');
                setParams({ view: data.view });
              }}
            >
              Reset filters
            </Button>
          </div>
        )}
      </form>
      {stage === 'pre_site' && (
        <Text size="sm" c="dimmed">
          Findings from the pre-site form review: measurements, proposed door,
          track and installation materials. Open a report for the original grade
          and findings.
        </Text>
      )}
      {stage === 'measurement_applicability' && (
        <Text size="sm" c="dimmed">
          Does the existing pre-site review still apply to this order and the
          current form? An unverified revision does not establish a change or a
          measurement failure.
        </Text>
      )}
      {stage === 'po' && (
        <Text size="sm" c="dimmed">
          Does the submitted purchase order match the customer’s signed, sold
          estimate and approved changes? This is separate from the pre-site
          form.
        </Text>
      )}
      {stage === 'acknowledgement' && (
        <Text size="sm" c="dimmed">
          Does the manufacturer’s acknowledgement agree with both the submitted
          PO and the signed, sold estimate with approved changes?
        </Text>
      )}
      <Group justify="space-between" gap="xs">
        <Text size="sm" fw={600}>
          {data.project_count}{' '}
          {data.project_count === 1 ? 'project' : 'projects'}
          {data.unlinked_job_count
            ? ` · ${data.unlinked_job_count} jobs without a project link`
            : ''}
        </Text>
        <Text size="xs" c="dimmed">
          One entry per project · top matching issue first
        </Text>
      </Group>
      <div>
        <div className="inbox-list-heading" aria-hidden="true">
          <span>Project / job</span>
          <span>Issue to review</span>
          <span>Action</span>
        </div>
        <ul className="inbox-list" aria-label="Projects needing review">
          {data.items.map((item) => (
            <li key={item.key}>
              <div className="inbox-row">
                <div className="inbox-project">
                  {item.project_id && item.project_available ? (
                    <Anchor
                      component={Link}
                      to={`/projects/${item.project_id}?from=${encodeURIComponent(returnTo)}`}
                      onClick={() => rememberPosition(returnTo)}
                      fw={600}
                    >
                      Project {item.display_number}
                    </Anchor>
                  ) : (
                    <Text fw={600}>
                      {item.project_id
                        ? `Project ${item.display_number}`
                        : `Job ${item.job_ids[0]}`}
                    </Text>
                  )}
                  {!!item.job_ids.length && item.project_id && (
                    <Text size="xs" c="dimmed">
                      Job{item.job_ids.length > 1 ? 's' : ''}{' '}
                      {item.job_ids.join(', ')}
                    </Text>
                  )}
                  {!item.project_id && (
                    <Text size="xs" c="dimmed">
                      Project link unavailable
                    </Text>
                  )}
                  {item.collection_membership === 'unconfirmed' &&
                    data.view !== 'current' && (
                      <Text size="xs" c="dimmed">
                        Collection unconfirmed
                      </Text>
                    )}
                  {item.alert_count > 1 && (
                    <button
                      type="button"
                      className="inbox-more-issues"
                      aria-expanded={expanded.includes(item.key)}
                      onClick={() =>
                        setExpanded((values) =>
                          values.includes(item.key)
                            ? values.filter((key) => key !== item.key)
                            : [...values, item.key],
                        )
                      }
                    >
                      {expanded.includes(item.key) ? (
                        <ChevronUp size={13} />
                      ) : (
                        <ChevronDown size={13} />
                      )}
                      {item.alert_count - 1} more issue
                      {item.alert_count > 2 ? 's' : ''}
                    </button>
                  )}
                </div>
                <div className="inbox-issue">
                  <Group gap={8} mb={5}>
                    <AlertLabel alert={item.top_alert} />
                    {!['pre_site', 'measurement_applicability'].includes(
                      item.top_alert.stage,
                    ) && (
                      <Text size="xs" c="dimmed">
                        {QUEUE_STAGE_LABELS[item.top_alert.stage]}
                      </Text>
                    )}
                  </Group>
                  <Text fw={600} className="inbox-headline">
                    {item.top_alert.source === 'pre_site'
                      ? item.top_alert.title
                      : reviewNarrative(item.top_alert.title)}
                  </Text>
                  <Text
                    size="sm"
                    c="gray.7"
                    lineClamp={2}
                    className="inbox-brief"
                  >
                    {item.top_alert.source === 'pre_site'
                      ? item.top_alert.brief || item.top_alert.summary
                      : reviewNarrative(
                          item.top_alert.brief || item.top_alert.summary,
                        )}
                  </Text>
                  {item.top_alert.order_ref && (
                    <Text size="xs" c="dimmed" mt={5}>
                      PO {item.top_alert.order_ref}
                    </Text>
                  )}
                </div>
                <div className="inbox-action">
                  <ReviewAction alert={item.top_alert} returnTo={returnTo} />
                </div>
              </div>
              {expanded.includes(item.key) && (
                <div
                  className="inbox-other-issues"
                  aria-label="Other matching issues"
                >
                  {item.alerts.slice(1).map((alert) => (
                    <div className="inbox-other-issue" key={alert.id}>
                      <div>
                        <Text size="xs" c="dimmed">
                          {QUEUE_STAGE_LABELS[alert.stage]}
                          {alert.order_ref ? ` · PO ${alert.order_ref}` : ''}
                          {alert.job_id ? ` · Job ${alert.job_id}` : ''}
                        </Text>
                        <Text fw={500} size="sm">
                          {alert.source === 'pre_site'
                            ? alert.title
                            : reviewNarrative(alert.title)}
                        </Text>
                      </div>
                      <ReviewAction alert={alert} returnTo={returnTo} />
                    </div>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
        {!data.items.length && (
          <div className="inbox-empty">
            <Title order={3}>No matching review items</Title>
            <Text c="dimmed" size="sm">
              Change the review area or filters, or browse all stored reviews.
            </Text>
            <Button
              variant="light"
              mt="md"
              onClick={() => {
                setSearch('');
                setParams({ view: 'all' });
              }}
            >
              Show all reviews
            </Button>
          </div>
        )}
      </div>
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          {data.total && data.items.length
            ? `${offset + 1}–${Math.min(offset + data.items.length, data.total)} of ${data.total}`
            : '0'}{' '}
          entries
        </Text>
        <Group gap="xs">
          <Button
            variant="default"
            size="sm"
            disabled={!offset}
            onClick={() =>
              update({ offset: String(Math.max(0, offset - 50)) }, true)
            }
          >
            Previous
          </Button>
          <Button
            variant="default"
            size="sm"
            disabled={offset + 50 >= data.total}
            onClick={() => update({ offset: String(offset + 50) }, true)}
          >
            Next
          </Button>
        </Group>
      </Group>
    </Stack>
  );
}
